import {
  Children,
  type ReactNode,
  createContext,
  isValidElement,
  useContext,
  useId,
} from 'react';

import { RiArrowRightSLine } from '@remixicon/react';

import { Text } from '@spotify-internal/encore-web';
import { useDisclosure } from '@spotify/confidence-core-react';

import { ElapsedTimer } from '../ElapsedTimer';

import styles from './ToolLabel.module.css';

type LabelPartProps = {
  children: ReactNode;
};

type SecondaryProps = LabelPartProps & {
  title?: string;
};

type ToolLabelContextValue = {
  hasDetails: boolean;
  expanded: boolean;
  detailsId: string;
  toggle: () => void;
};

const ToolLabelContext = createContext<ToolLabelContextValue | null>(null);

const Details = ({ children }: { children: ReactNode }) => {
  const context = useContext(ToolLabelContext);
  if (!context) return null;

  return (
    <div
      id={context.detailsId}
      className={styles.details}
      hidden={!context.expanded}
    >
      {context.expanded && children}
    </div>
  );
};

const Root = ({ children }: { children: ReactNode }) => {
  const [expanded, { toggle }] = useDisclosure();
  const detailsId = useId();
  const hasDetails = Children.toArray(children).some(
    child => isValidElement(child) && child.type === Details,
  );

  return (
    <ToolLabelContext.Provider
      value={{ hasDetails, expanded, detailsId, toggle }}
    >
      <Text
        as="div"
        variant="bodySmall"
        className={styles.root}
        data-has-details={hasDetails}
      >
        {children}
      </Text>
    </ToolLabelContext.Provider>
  );
};

const Primary = ({ children }: LabelPartProps) => {
  const context = useContext(ToolLabelContext);
  if (!context?.hasDetails) {
    return <span className={styles.primary}>{children}</span>;
  }

  return (
    <button
      type="button"
      className={styles.trigger}
      aria-expanded={context.expanded}
      aria-controls={context.detailsId}
      onClick={context.toggle}
    >
      <span className={styles.primary}>{children}</span>
      <RiArrowRightSLine size={16} className={styles.chevron} />
    </button>
  );
};

const Secondary = ({ children, title }: SecondaryProps) => (
  <span className={styles.secondary} title={title}>
    {children}
  </span>
);

const Timer = ({ startedAt }: { startedAt?: number }) => {
  if (startedAt === undefined) return null;
  return (
    <span className={styles.timer}>
      <ElapsedTimer startedAt={startedAt} />
    </span>
  );
};

export const ToolLabel = { Root, Primary, Secondary, Timer, Details };
